import {
    SenkronPlan,
    SenkronPlanAlan,
    SenkronPlanDosya,
    SenkronPlanGovde
} from "@/services/senkronPlanService";

/**
 * Plan formu <-> uç gövdesi dönüşümleri ve istemci tarafı ön-denetim
 * (Sorgu Snapshot Faz 3, DEV-2966).
 *
 * Bileşenden AYRI bir modül: repoda kanıtlanmış desen bu (`sorguOneriKopru.ts`,
 * `anketSso.ts`) — `.vue` dosyaları mount edilmeden test edilemiyor, saf
 * fonksiyonlar edilebiliyor.
 */

// kod inceleme: dışa aktarımı hiçbir tüketici import etmiyordu (yalnız bu
// dosyadaki saatiEkranBicimine/bosPlanFormu kullanıyor) — modül içine alındı.
/** F3-D6: sunucu varsayılanı. Form da bununla açılır. */
const VARSAYILAN_KOSUM_SAATI = "00:15";

/** `SenkronPlanKaydetRequest`: `ad` => string|max:120. */
export const AZAMI_AD_UZUNLUGU = 120;

/** `alanlar.*.frekans_gun` => integer|min:1|max:365. 1 = her gece. */
export const ASGARI_FREKANS = 1;
export const AZAMI_FREKANS = 365;

/**
 * `dosya_secimi.dosyalar` => max:config(sorgu_snapshot.senkron.azami_dosya).
 *
 * Sunucu config'i istemciye AÇILMIYOR; buradaki değer sunucu varsayılanını
 * (500) yansıtır — otorite YİNE DE sunucudur, config'i değiştirirlerse burası
 * eskir. DEV-2603 B25 kararıyla artık İKİ yerde kullanılıyor: (1)
 * `SenkronDosyaSecDialog`da erken uyarı/"Tamam"ı kilitleme, (2) aşağıda
 * `planGecerliMi()`de SERT kural — aksi halde Kaydet sunucudan 422 yiyip
 * global 422 gösterimine düşerdi. Sabit TEK yerde tutuluyor ki iki kullanım
 * birbirinden kayıp yanlış bir tavan göstermesin.
 */
export const AZAMI_DOSYA_UYARI_ESIGI = 500;

/** `kosum_saati` => regex:/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/ */
const SAAT_REGEX = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/;

/** Ekranda düzenlenen plan. Uç gövdesiyle bilerek birebir DEĞİL (saat biçimi, dosya düzlemi). */
export interface SenkronPlanFormu {
    ad: string;
    aktif: boolean;
    /** Ekran biçimi: "HH:MM". Gövdeye yazarken saniye eklenir. */
    kosumSaati: string;
    ucretliSorguyaDevam: boolean;
    alanlar: Array<SenkronPlanAlan>;
    dosyalar: Array<SenkronPlanDosya>;
}

export function saatGecerliMi(saat: string): boolean {
    return typeof saat === "string" && SAAT_REGEX.test(saat);
}

/** "00:15:00" -> "00:15". Uç MySQL TIME kolonunu saniyeli döndürüyor. */
export function saatiEkranBicimine(saat: string | null | undefined): string {
    if (!saat) return VARSAYILAN_KOSUM_SAATI;
    return String(saat).slice(0, 5);
}

/**
 * "00:15" -> "00:15:00".
 *
 * SANİYE ŞART. `senkron_plan.kosum_saati` MySQL TIME kolonudur ve cevapta
 * "00:15:00" gelir; model bunu bilerek cast etmiyor (ham dize kalıyor). İstemci
 * düzenleme sırasında "00:15" geri gönderirse Eloquent `isDirty('kosum_saati')`
 * TRUE olur — saat hiç değişmemiş olsa bile. Sunucu bunu "üretimi etkileyen
 * değişiklik" sayıp gecenin paketini iptal edip yeniden üretir; kullanıcının
 * görev listesinde sahte bir iptal paketi belirir. Saniyeyi eklemek bu yanlış
 * pozitifi kapatır.
 */
export function saatiSunucuBicimine(saat: string): string {
    const temiz = String(saat ?? "").trim();
    if (!saatGecerliMi(temiz)) return temiz;
    return temiz.length === 5 ? temiz + ":00" : temiz;
}

/**
 * "en fazla N dosya" aşım cümlesi.
 *
 * TEK YERDE tutuluyor: hem `planGecerliMi()` hem de `SenkronDosyaSecDialog`ın
 * Adım 3 uyarısı AYNI CÜMLEYİ göstermeli — ikisi ayrı ayrı yazılsaydı biri
 * değişip diğeri unutulabilirdi (DEV-2603 B25).
 */
export function dosyaSayisiAsimMesaji(
    toplamSonrasi: number,
    azami: number = AZAMI_DOSYA_UYARI_ESIGI
): string {
    const fazla = toplamSonrasi - azami;
    return `Bir plan en fazla ${azami} dosya alır; ${fazla} dosya fazla. `
        + "Fazlasını çıkarın ya da kalan dosyalar için ikinci bir plan oluşturun.";
}

export function bosPlanFormu(): SenkronPlanFormu {
    return {
        ad: "",
        aktif: true,
        kosumSaati: VARSAYILAN_KOSUM_SAATI,
        // F3 §2 ücret güvenliği: varsayılan KAPALI. Kullanıcı açıkça açmalı.
        ucretliSorguyaDevam: false,
        alanlar: [],
        dosyalar: []
    };
}

/** Uçtan gelen planı forma çevirir (düzenleme dialogu bununla açılır). */
export function plandanForm(plan: SenkronPlan): SenkronPlanFormu {
    return {
        ad: plan.ad ?? "",
        aktif: plan.aktif !== false,
        kosumSaati: saatiEkranBicimine(plan.kosum_saati),
        ucretliSorguyaDevam: plan.ucretli_sorguya_devam === true,
        alanlar: (plan.alanlar ?? []).map((alan) => ({
            name: alan.name,
            frekans_gun: Number(alan.frekans_gun)
        })),
        dosyalar: (plan.dosya_secimi?.dosyalar ?? []).map((dosya) => ({
            birim_id: String(dosya.birim_id),
            dosya_esas_no: String(dosya.dosya_esas_no),
            birim_adi: dosya.birim_adi
        }))
    };
}

/**
 * Formu uç gövdesine çevirir.
 *
 * HİÇBİR ALANA `null` YAZILMAZ. Sözleşme gereği gövdedeki `null` "gönderilmedi"
 * demektir, "sıfırla" değil; `undefined` ise `JSON.stringify` tarafından
 * anahtarıyla birlikte düşürülür. `x ?? null` / `x || null` kalıbı bu dosyada
 * yasaktır — `birim_adi` bile boşsa gönderilmez.
 */
export function formuGovdeyeCevir(form: SenkronPlanFormu): SenkronPlanGovde {
    return {
        ad: form.ad.trim(),
        aktif: form.aktif,
        kosum_saati: saatiSunucuBicimine(form.kosumSaati),
        ucretli_sorguya_devam: form.ucretliSorguyaDevam,
        alanlar: form.alanlar.map((alan) => ({
            name: alan.name,
            frekans_gun: Number(alan.frekans_gun)
        })),
        dosya_secimi: {
            tip: "liste",
            dosyalar: form.dosyalar.map((dosya) => {
                const satir: SenkronPlanDosya = {
                    birim_id: String(dosya.birim_id).trim(),
                    dosya_esas_no: String(dosya.dosya_esas_no).trim()
                };
                const ad = (dosya.birim_adi ?? "").trim();
                if (ad) satir.birim_adi = ad;
                return satir;
            })
        }
    };
}

/**
 * İstemci tarafı ön-denetim: kullanıcıya gösterilecek hata metinleri.
 *
 * Sunucu YİNE DE otoritedir (`SenkronPlanKaydetRequest`); bu yalnız gereksiz bir
 * 422 turunu ve "kaydet"e basıp hiçbir şey olmamasını önler. Boş dizi = geçerli.
 */
export function planGecerliMi(form: SenkronPlanFormu): string[] {
    const hatalar: string[] = [];

    if (!form.ad || !form.ad.trim()) {
        hatalar.push("Plan adı boş bırakılamaz.");
    } else if (form.ad.trim().length > AZAMI_AD_UZUNLUGU) {
        hatalar.push(`Plan adı en çok ${AZAMI_AD_UZUNLUGU} karakter olabilir.`);
    }

    if (!saatGecerliMi(form.kosumSaati)) {
        hatalar.push("Koşum saati SS:DD biçiminde olmalıdır.");
    }

    if (!form.alanlar.length) {
        hatalar.push("En az bir sorgu alanı seçilmelidir.");
    }

    const gorulenAlan: Record<string, boolean> = {};
    form.alanlar.forEach((alan) => {
        const frekans = Number(alan.frekans_gun);
        if (!Number.isInteger(frekans) || frekans < ASGARI_FREKANS || frekans > AZAMI_FREKANS) {
            hatalar.push(
                `"${alan.name}" alanının frekansı ${ASGARI_FREKANS}-${AZAMI_FREKANS} gün arasında olmalıdır.`
            );
        }
        if (gorulenAlan[alan.name]) {
            hatalar.push(`Aynı alan birden çok kez planlanamaz: ${alan.name}`);
        }
        gorulenAlan[alan.name] = true;
    });

    if (!form.dosyalar.length) {
        hatalar.push("En az bir dosya seçilmelidir.");
    } else if (form.dosyalar.length > AZAMI_DOSYA_UYARI_ESIGI) {
        // `SenkronDosyaSecDialog` Adım 3'te "Tamam"ı zaten kilitliyor; bu SON
        // hat — dialog'u atlayan ya da eski bir formu Kaydet'e basan kullanıcı
        // burada 422'den ÖNCE durdurulur.
        hatalar.push(dosyaSayisiAsimMesaji(form.dosyalar.length));
    }

    const gorulenDosya: Record<string, boolean> = {};
    form.dosyalar.forEach((dosya) => {
        const anahtar = `${String(dosya.birim_id).trim()}:${String(dosya.dosya_esas_no).trim()}`;
        if (gorulenDosya[anahtar]) {
            hatalar.push(`Aynı dosya birden çok kez seçilemez: ${anahtar}`);
        }
        gorulenDosya[anahtar] = true;
    });

    return hatalar;
}

/** `SenkronPlanDialog`in çağıracağı store eylemi. */
type PlanKaydetEylemi = "senkronPlan/planGuncelle" | "senkronPlan/planOlustur";

/**
 * Kaydetmede hangi store eyleminin çağrılacağı.
 *
 * Bileşenden çıkarıldı çünkü yanlış seçimin bedeli ağır ve sessiz: düzenlenen bir
 * plan için `planOlustur` çağrılsaydı backend'de İKİNCİ bir plan açılır ve aynı
 * dosyalar gece boyunca İKİ KEZ sorgulanırdı (ücretli alanlarda çift ücret);
 * ters yönde ise yeni plan `id`siz bir PATCH'e gidip 404 alırdı. Repo kuralı
 * gereği `.vue` dosyaları test edilmiyor, bu karar ise test edilmeli.
 */
export function planKaydetEylemi(plan: { id?: number } | null | undefined): PlanKaydetEylemi {
    return plan && plan.id ? "senkronPlan/planGuncelle" : "senkronPlan/planOlustur";
}

/**
 * Cevabın `sebep` koduna karşılık gelen görsel ton.
 *
 * METİN ÜRETMEZ — kullanıcıya gösterilecek cümle sunucunun `mesaj` alanıdır.
 * Burada yalnız kullanıcının cümleyi "iyi haber mi kötü haber mi" diye bir
 * bakışta ayırmasını sağlayan renk seçilir.
 */
export function sebepTonu(sebep: string | undefined): "success" | "info" | "warning" {
    switch (sebep) {
        case "uretildi":
        case "yeniden_uretildi":
        case "iptal_edildi":
            return "success";
        case "uretim_hatasi":
        case "paket_bulunamadi":
        case "eszamanli_islem":
            return "warning";
        default:
            // saat_gecti, kosum_basladi, bugun_uretilmedi, telafi_bekliyor,
            // degisiklik_yok, zaten_uretildi, plan_pasif — hepsi bilgi.
            return "info";
    }
}
